import React, { FunctionComponent, useState, useEffect, ChangeEvent, useMemo } from 'react'
import { Link, Box, Typography } from '@mui/material'
import { Download as DownloadIcon } from '@mui/icons-material'
import { FloatingSelect } from '@icanbwell/ui-toolkit'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { HEADER_HEIGHT } from '@client-hub/shared/constants/styles'
import { useCompanyListQuery, useUserAssetMutation } from '@client-hub/store/api/crs/react'
import ROUTES from '@client-hub/shared/constants/routes'
import { useDispatch, useSelector } from 'react-redux'
import { selectTargetGroup } from '@client-hub/store/slices/metadata/selectors'
import { UserAssetResponse } from '@client-hub/store/api/crs/types'
import { updateSelectedGroupId } from '@client-hub/store/slices/metadata'
import {
  AssetContainer,
  AssetDocumentCard,
  AssetImgCard,
  AssetImgCardOverlay,
  HeaderWrapper,
  MainHeading,
  NoAssetPlaceholder,
} from '../components/UserAssets/styles'
import { schemas } from '@client-hub/store/api/crs/types'

type userAsset = {
  asset_content: string
  type: string
  file_name: string
  name: string
}[]

/**
 * Method to get all the information of selected Group
 * @param groupId  contain the id of selected Group
 * @param groupList  array containing list of grooups
 * @returns object containes all the information about selected group
 */
const getSelectedGroup = (groupId: string | undefined, groupList: schemas['CompanyListSchema'] = []) => {
  const selectedGroup = groupList?.find((value) => {
    return value.id?.toString() === groupId
  })
  return selectedGroup
}
/**
 * @description
 * @param groupList  array containing list of grooups
 * method to reshuffle array for display purpose.
 */
const reShuffleGroupList = (groupList: (schemas['CompanyItemSchema'] & { has_parent?: boolean })[] = []) => {
  const tempGroupList = []
  if (groupList && groupList.length) {
    for (let i = 0; i < groupList.length; i++) {
      // loop through groups
      if (groupList[i].is_parent) {
        // find parent group
        tempGroupList.push(groupList[i])
        for (let j = 0; j < groupList.length; j++) {
          if (!groupList[j].is_parent && groupList[j].parent_id === groupList[i].id) {
            groupList[j].has_parent = true // adding a key to notify it has a parent in the list
            tempGroupList.push(groupList[j])
          }
        }
      }
    }
    if (groupList.length > tempGroupList.length) {
      // part of some child compant but not its parent
      for (let i = 0; i < groupList.length; i++) {
        // loop through groups
        let ispart = false

        for (let j = 0; j < tempGroupList.length; j++) {
          if (groupList[i].id === tempGroupList[j].id) {
            ispart = true
            break
          }
        }
        if (!ispart) {
          // this groupList index is not a part of the tempgrouplist
          // adding a key to notify it does not have a parent in the list
          tempGroupList.push({
            ...groupList[i],
            has_parent: false,
          })
        }
      }
    }
  }
  return tempGroupList
}
/**
 * @description
 * method to get user's asset.
 */
const getAsset = (
  itemId: string,
  itemType: string,
  userId: string,
  selectedGroupId: string | undefined,
  getUserAsset: any,
  userAssetData: UserAssetResponse,
  selectedGroup: any = '',
): userAsset => {
  let groupId: string | undefined
  if (Object.keys(selectedGroup).length === 0) {
    // If we are calling get asset before fetching groups list we need to get group id from local storage
    groupId = selectedGroupId
  } else {
    groupId = selectedGroup['id']
  }
  getUserAsset({ itemId: itemId, itemType: itemType, userId: userId, groupId: groupId || '' })
  if (userAssetData?.results && userAssetData?.results.length) {
    return userAssetData?.results
  } else {
    return []
  }
}
const UserAsset: FunctionComponent = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [fetchOnGroupListing, setFetchOnGroupListing] = useState<boolean>(false)
  let { data: { results: groupList } = {} } = useCompanyListQuery(undefined, { skip: !fetchOnGroupListing })
  const [getUserAsset, { isLoading, data: userAssetData }] = useUserAssetMutation()
  const selectedGroupId = useSelector(selectTargetGroup)
  const userId = searchParams.get('userId') || ''
  const itemId = searchParams.get('itemId') || ''
  const itemType = searchParams.get('itemType') || ''
  const navigate = useNavigate()
  let userAsset: userAsset = []

  const [parentGroupId, setParentGroupId] = useState('')
  const [isLoaderShown, setIsLoaderShown] = useState<boolean>(false)
  const [assetNotFoundMessage, setAssetNotFoundMessage] = useState<string>('Not Found')
  const [fetchedAssetOnInit, setFetchedAssetOnInit] = useState(false)
  userAsset = userAssetData?.results || []
  const dispatch = useDispatch()

  useEffect(() => {
    if (!selectedGroupId) {
      navigate(ROUTES.DASHBOARD)
    }
  }, [])

  useEffect(() => {
    setIsLoaderShown(isLoading)
  }, [isLoading])

  useEffect(() => {
    if (groupList) {
      const parentGroup =
        groupList?.find((group) => {
          return group.is_parent
        }) || groupList[0]

      if (selectedGroupId) {
        setParentGroupId(selectedGroupId)
      } else {
        parentGroup.id && setParentGroupId(parentGroup.id.toString())
      }
    }
  }, [groupList])

  useEffect(() => {
    // method calls to get user's asset
    const selectedGroup = getSelectedGroup(selectedGroupId, groupList)
    if (selectedGroup && Object.keys(selectedGroup)?.length !== 0) {
      if (itemType === 'contract') {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        userAsset = getAsset(
          itemId,
          itemType,
          userId,
          selectedGroupId,
          getUserAsset,
          userAssetData as UserAssetResponse,
          selectedGroup,
        )
      }
      setFetchedAssetOnInit(true)
    }

    getGroupsList()
    if (itemType === 'contract') {
      setAssetNotFoundMessage(
        "Contract doesn't exist or it doesn't belong to this group. Try changing the group from the dropdown",
      )
    }
  }, [parentGroupId])

  /**
   * @description
   * Method to change the group
   */
  const onGroupChange = (e: ChangeEvent<HTMLInputElement>) => {
    dispatch(updateSelectedGroupId(e.target.value))
    setParentGroupId(e.target.value)
  }

  /**
   * @description
   * Method for mapping the groupList for display purpose
   */
  const groupOptions = useMemo(() => {
    if (!groupList?.length) return [{ label: t('routes.userAsset.notFound'), value: '' }]

    return groupList.map((group) => ({
      label: group.group_name || '',
      value: group.id!,
    }))
  }, [groupList])

  /**
   * @description
   *  method to retrieve all available groups of which the user is admin.
   */
  const getGroupsList = () => {
    setFetchOnGroupListing(true)
    // fetch the groupsList

    // reshuffle array for display purpose
    const tmpGroupList = reShuffleGroupList(groupList)
    groupList = tmpGroupList
    if (!fetchedAssetOnInit) {
      userAsset = getAsset(itemId, itemType, userId, selectedGroupId, getUserAsset, userAssetData as UserAssetResponse)
    }
  }

  /**
   * @description
   * method to download the user asset
   */
  const downloadAsset = (e: React.MouseEvent<HTMLAnchorElement>, url: string, name: string): void => {
    e.preventDefault()
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', name)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Box height="100%" width="100%" sx={{ backgroundColor: '#fafafa', minHeight: `calc(100vh - ${HEADER_HEIGHT})` }}>
      <HeaderWrapper position="relative">
        <MainHeading sx={{ margin: '.5rem 2.5rem' }} variant="h1">
          {t('routes.userAsset.title')}
        </MainHeading>
        <Box width={300} sx={{ margin: '.5rem 2.5rem' }}>
          <FloatingSelect
            name="group"
            value={parentGroupId}
            label={t('routes.userAsset.selectGroup')}
            options={groupOptions}
            onChange={onGroupChange}
            icon="DropdownArrow"
          />
        </Box>
      </HeaderWrapper>
      {!isLoaderShown &&
        (!userAsset || !userAsset.length ? (
          <NoAssetPlaceholder>{assetNotFoundMessage}</NoAssetPlaceholder>
        ) : userAsset[0] && userAsset[0].type === 'doc' ? (
          <AssetContainer sx={{ maxWidth: '210px' }}>
            <Link
              sx={{ cursor: 'pointer' }}
              onClick={(e) =>
                downloadAsset(
                  e as React.MouseEvent<HTMLAnchorElement>,
                  userAsset[0].asset_content,
                  userAsset[0].file_name,
                )
              }
            >
              <AssetDocumentCard>
                <DownloadIcon />
                <p>{t('routes.userAsset.downloadDoc')}</p>
              </AssetDocumentCard>
            </Link>
          </AssetContainer>
        ) : (
          userAsset.map((asset) => (
            <AssetContainer key={asset.name}>
              <Typography>{asset.name}</Typography>
              <Link
                sx={{ cursor: 'pointer' }}
                onClick={(e) =>
                  downloadAsset(e as React.MouseEvent<HTMLAnchorElement>, asset.asset_content, asset.file_name)
                }
              >
                <AssetImgCard>
                  <AssetImgCardOverlay className="downloadOverlay">
                    <DownloadIcon />
                    {t('routes.userAsset.clickDownload')}
                  </AssetImgCardOverlay>
                  <img src={asset.asset_content} alt={asset.name} />
                </AssetImgCard>
              </Link>
            </AssetContainer>
          ))
        ))}
    </Box>
  )
}

export default UserAsset
